import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Search, Send, Wrench, Phone, Mail, Check } from "lucide-react";
import { api, errMsg, fmt, fmtDate } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { VoicePlayer } from "@/components/VoiceRecorder";
import { ReactionBadge } from "@/components/Reactions";
import { Modal } from "@/components/ui-bits";
import { MessagingTools } from "@/components/MessagingTools";

const COMMUNITY = "__community__";
const sameDay = (a, b) => a && b && a.slice(0, 10) === b.slice(0, 10);
const dayLabel = (iso) => { const d = new Date(iso), t = new Date(); const diff = Math.round((Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate()) - Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())) / 86400000); return diff === 0 ? "Today" : diff === 1 ? "Yesterday" : fmtDate(iso, { weekday: "short", day: "numeric", month: "short" }); };
const when = (iso) => { if (!iso) return ""; const d = new Date(iso), t = new Date(); return d.toDateString() === t.toDateString() ? fmt(iso, { hour: "2-digit", minute: "2-digit" }) : fmtDate(iso); };

export const Bubble = ({ m, mine, testId, children }) => (
  <div className={`bubble-row ${mine ? "mine" : ""}`} data-testid={testId}>
    <div className="bubble">
      <div className="bubble-txt">{m.body}</div>
      {m.voiceNoteId && <VoicePlayer id={m.voiceNoteId} testId={`${testId}-voice`} />}
      {children}
      <time className="bubble-time">{fmt(m.createdAt, { hour: "2-digit", minute: "2-digit" })}{m.channels ? ` · ${m.channels.join(" + ").toLowerCase()}` : ""}{m.status === "failed" ? " · failed" : ""}</time>
    </div>
  </div>
);

export const Thread = ({ messages, isMine, emptyText, idPrefix = "chat-msg", renderExtra }) => {
  const boxRef = useRef();
  useEffect(() => { const el = boxRef.current; if (el) el.scrollTop = el.scrollHeight; }, [messages]);
  if (!messages) return <div className="center"><span className="spin" /></div>;
  return (
    <div className="chat-scroll" ref={boxRef} data-testid={`${idPrefix}-scroll`}>
      {messages.length === 0 && <div className="chat-empty" data-testid={`${idPrefix}-empty`}>{emptyText}</div>}
      {messages.map((m, i) => (
        <div key={m.id}>
          {!sameDay(messages[i - 1]?.createdAt, m.createdAt) && <div className="chat-day"><span>{dayLabel(m.createdAt)}</span></div>}
          <Bubble m={m} mine={isMine(m)} testId={`${idPrefix}-${m.id}`}>{renderExtra?.(m)}</Bubble>
        </div>
      ))}
    </div>
  );
};

export const Composer = ({ onSend, placeholder, disabled, testId = "chat", confirmLabel }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    const body = text.trim(); if (!body || busy) return;
    if (confirmLabel && !window.confirm(confirmLabel)) return;
    setBusy(true);
    try { await onSend(body); setText(""); } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <form className="chat-compose" onSubmit={submit} data-testid={`${testId}-compose`}>
      <textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} disabled={disabled}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }} data-testid={`${testId}-input`} />
      <button className="btn chat-send" disabled={disabled || busy || !text.trim()} aria-label="Send" data-testid={`${testId}-send`}><Send size={16} /></button>
    </form>
  );
};

const ConvCard = ({ c, active, onClick }) => (
  <button type="button" className={`conv ${active ? "on" : ""} ${c.unread ? "unread" : ""}`} onClick={onClick} data-testid={`conv-${c.userId}`}>
    <Avatar client={{ id: c.clientId, photoKey: c.photoKey, name: c.name }} size={40} radius={10} />
    <div className="conv-body">
      <div className="conv-top"><b data-testid={`conv-name-${c.userId}`}>{c.name}</b><span className="conv-time">{when(c.lastAt)}</span></div>
      <div className="conv-prev">{c.unread > 0 && <i className="conv-dot" data-testid={`conv-unread-${c.userId}`} />}<span>{c.lastBody ? `${c.lastDirection === "INBOUND" ? "" : "You: "}${c.lastBody}` : "No messages yet"}</span></div>
    </div>
  </button>
);

const ClientChat = ({ userId, onSent }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/conversations/${userId}/messages`).then((r) => { setD(r.data); window.dispatchEvent(new Event("unread-changed")); }), [userId]);
  useEffect(() => { setD(null); load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  const send = async (body) => { await api.post(`/conversations/${userId}/messages`, { body }); await load(); onSent(); };
  if (!d) return <div className="center"><span className="spin" /></div>;
  const c = d.client;
  return (
    <>
      <div className="chat-head" data-testid="chat-head">
        <Avatar client={{ id: c.id, photoKey: c.photoKey, name: c.name }} size={40} radius={10} />
        <div className="sb"><div className="st" data-testid="chat-head-name">{c.name}</div><div className="ss">{c.phone && <><Phone size={11} /> {c.phone} · </>}{c.email && <><Mail size={11} /> {c.email} · </>}replies via {c.preferredChannel.toLowerCase()}</div></div>
        <a className="btn sm g" href={`/admin/clients#${c.id}`} data-testid="chat-open-profile">Profile</a>
      </div>
      <Thread messages={d.messages} isMine={(m) => m.direction !== "INBOUND"} emptyText={`Say hello to ${c.name.split(" ")[0]} — session notes, check-ins and form links all land here.`}
        renderExtra={(m) => m.direction !== "INBOUND" && m.reaction ? <ReactionBadge message={m} testId={`chat-msg-reaction-${m.id}`} /> : null} />
      <Composer onSend={send} placeholder={`Message ${c.name.split(" ")[0]}… (Enter to send, Shift+Enter for a new line)`} />
    </>
  );
};

const CommunityChat = ({ recipients, onSent }) => {
  const [anns, setAnns] = useState(null);
  const load = useCallback(() => api.get("/announcements").then((r) => setAnns(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const send = async (body) => { const r = await api.post("/announcements", { body }); toast.success(`Announcement sent to ${r.data.recipients} clients`); await load(); onSent(); };
  return (
    <>
      <div className="chat-head" data-testid="chat-head">
        <span className="conv-mega" style={{ width: 40, height: 40 }}><Megaphone size={20} /></span>
        <div className="sb"><div className="st" data-testid="chat-head-name">Community — All Clients</div><div className="ss">Broadcast only · goes to {recipients} active client{recipients !== 1 ? "s" : ""} · clients can't reply here</div></div>
      </div>
      <Thread messages={anns} isMine={() => true} idPrefix="announcement" emptyText="No announcements yet. Schedule changes, challenges, promos — post once, everyone gets it."
        renderExtra={(m) => <span className="ss" style={{ display: "block", marginTop: 4 }}><Check size={11} /> {m.sent}/{m.recipients} delivered · by {m.byName}</span>} />
      <Composer onSend={send} testId="announcement" placeholder={`Announce to all ${recipients} clients…`} confirmLabel={`Send this announcement to all ${recipients} active clients?`} disabled={!recipients} />
    </>
  );
};

export default function Messages() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  const [tools, setTools] = useState(false);
  const load = useCallback(() => api.get("/conversations").then((r) => setData(r.data)), []);
  useEffect(() => { load(); const t = setInterval(load, 20000); window.addEventListener("unread-changed", load); return () => { clearInterval(t); window.removeEventListener("unread-changed", load); }; }, [load]);
  const list = useMemo(() => (data?.conversations || []).filter((c) => c.name.toLowerCase().includes(q.trim().toLowerCase())), [data, q]);
  return (
    <div className="chat fade" data-testid="admin-messages">
      <aside className="chat-list" data-testid="conversation-list">
        <div className="chat-list-head">
          <h3 style={{ margin: 0 }}>Messages{data?.totalUnread ? <span className="chip ok num" style={{ marginLeft: 8 }} data-testid="total-unread">{data.totalUnread}</span> : null}</h3>
          <button className="btn sm g" onClick={() => setTools(true)} title="Delivery channels & tools" data-testid="messaging-tools-button"><Wrench size={13} /></button>
        </div>
        <div className="chat-search"><Search size={14} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search conversations" data-testid="conversation-search" /></div>
        <button type="button" className={`conv pinned ${active === COMMUNITY ? "on" : ""}`} onClick={() => setActive(COMMUNITY)} data-testid="conv-community">
          <span className="conv-mega"><Megaphone size={18} /></span>
          <div className="conv-body">
            <div className="conv-top"><b>Community — All Clients</b><span className="conv-time">{when(data?.community?.lastAt)}</span></div>
            <div className="conv-prev"><span>{data?.community?.lastBody || `Broadcast to ${data?.community?.recipients ?? "all"} clients`}</span></div>
          </div>
        </button>
        <div className="chat-list-scroll">
          {!data ? <div className="center"><span className="spin" /></div> : list.length === 0 ? <div className="chat-empty" data-testid="conversation-empty">No conversations match.</div> :
            list.map((c) => <ConvCard key={c.userId} c={c} active={active === c.userId} onClick={() => setActive(c.userId)} />)}
        </div>
      </aside>
      <section className="chat-pane" data-testid="chat-pane">
        {!active && <div className="chat-empty tall" data-testid="chat-pane-empty"><Megaphone size={28} style={{ opacity: .4 }} /><b>Pick a conversation</b><span>Client chats are private threads. Community goes to everyone at once.</span></div>}
        {active === COMMUNITY && <CommunityChat recipients={data?.community?.recipients || 0} onSent={load} />}
        {active && active !== COMMUNITY && <ClientChat userId={active} onSent={load} />}
      </section>
      {tools && <Modal title="DELIVERY CHANNELS & TOOLS" sub="Live status, test sends and the inbound-reply simulator" onClose={() => setTools(false)} testId="messaging-tools-modal"><MessagingTools onChanged={load} /></Modal>}
    </div>
  );
}
